
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Tag, Tooltip } from "antd";
import { useMediaQuery } from "@mui/material";
import { Empty } from 'antd';
import { useMaterialUIController } from "context";

function LocationVisitsCard({ title, headers, items, type, height }) {
  // Responsive handling
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const isMobile = useMediaQuery("(max-width:600px)");

  return (
    <Card
      sx={{
        height: height || "400px",
        overflow: "auto",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        borderRadius: "12px",
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '10px',
          '&:hover': {
            background: '#555',
          },
        },
      }}
    >
      {/* Card Title */}
      <MDBox
        py={2.5}
        px={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderBottom: '1px solid',
          borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
        }}
      >
        <MDTypography variant="h5" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
          {title}
        </MDTypography>
      </MDBox>

      {/* Header Row */}
      <MDBox
        display="flex"
        px={3}
        py={1.5}
        sx={{
          backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        {headers.map((header, index) => (
          <MDTypography
            key={index}
            variant="caption"
            fontWeight="bold"
            color="text"
            sx={{
              flex: header.flex || 1,
              textAlign: index === (headers.length - 1) ? "center" : "left",
              display: isMobile && header.label === 'Energy' ? 'none' : 'inline',
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              letterSpacing: '0.5px',
              opacity: 0.8,
            }}
          >
            {header.label}
          </MDTypography>
        ))}
      </MDBox>

      {/* List Items */}
      <MDBox
        sx={{
          flex: 1,                    
          overflowY: "auto",
          color: darkMode ? "white" : "black",

          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: darkMode ? '#2c2c2c' : '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
            '&:hover': {
              background: '#555',
            },
          },
        }}
      >
        {
          items?.length === 0 ? (
            <MDBox display="flex" justifyContent="center" py={6}>
              <Empty description="No data available" />
            </MDBox>
          ) : (
            Array.isArray(items) && items.map((item, index) => (
              <MDBox
                key={index}
                display="flex"
                alignItems="center"
                px={3}
                py={1.5}
                sx={{
                  transition: 'all 0.2s ease',
                  borderBottom: '1px solid',
                  borderColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  '&:hover': {
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    transform: 'translateX(4px)',
                  },
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                {!item.type && (
                  <MDBox
                    sx={{
                      flex: headers[0].flex || 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      sx={{
                        fontSize: '0.75rem',
                        color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)',
                        backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        borderRadius: '6px',
                        padding: '4px 8px',
                        minWidth: '32px',
                        textAlign: 'center',
                      }}
                    >
                      {index + 1 < 10 ? `0${index + 1}` : index + 1}
                    </MDTypography>
                  </MDBox>
                )}

                {item.type && (
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      flex: headers[0].flex || 1,
                      textAlign: "left",
                      fontSize: '0.85rem',
                    }}
                  >
                    {item.type}
                  </MDTypography>
                )}
                <Tooltip title={item.name} placement="top">
                  <MDTypography
                    variant="body2"
                    fontWeight="medium"
                    sx={{
                      flex: headers[1].flex || 2,
                      textAlign: "left",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      fontSize: '0.875rem',
                      cursor: 'default',
                    }}
                  >
                    {item.name}
                  </MDTypography>
                </Tooltip>

                {!item.type && (
                  <MDTypography
                    variant="body2"
                    fontWeight="medium"
                    sx={{
                      display: isMobile ? 'none' : 'inline',
                      flex: headers[2].flex || 2,
                      textAlign: "left",
                      fontSize: '0.875rem',
                      color: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)',
                    }}
                  >
                    {item.energy || 'NA'}
                  </MDTypography>
                )}

                {!item.type && (
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      flex: headers?.[3]?.flex || 3,
                      textAlign: "left",
                      fontSize: '0.875rem',
                      color: darkMode ? '#66BB6A' : '#2e7d32',
                    }}
                  >
                    {item.revenue}
                  </MDTypography>
                )}

                <MDBox
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  sx={{
                    flex: headers[4]?.flex || 1,
                  }}
                >
                  <Tag
                    color={item.color}
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      padding: '4px 12px',
                      borderRadius: '12px',
                    }}
                  >
                    {item.visits}
                  </Tag>
                </MDBox>
              </MDBox>
            ))
          )
        }
      </MDBox>
    </Card>
  );
}

LocationVisitsCard.propTypes = {
  title: PropTypes.string.isRequired,
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      flex: PropTypes.number, // Optional for flexible column widths
    })
  ).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      energy: PropTypes.string.isRequired,
      revenue: PropTypes.string.isRequired,
      visits: PropTypes.number.isRequired,
    })
  ).isRequired,
  type: PropTypes.string.isRequired,
};

export default LocationVisitsCard;